import React, { useEffect, useMemo, useState } from "react";
import { submissionsApi } from "../services/externalPlatformApi";

const TABS = [
  { id: "mix", label: "Mixes and tracks" },
  { id: "roster", label: "Roster applications" },
];

const STATUSES = ["PENDING", "APPROVED", "REJECTED"];

export default function AdminSubmissions() {
  const [tab, setTab] = useState("mix");
  const [mixes, setMixes] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const visible = useMemo(() => (tab === "mix" ? mixes : applications), [applications, mixes, tab]);

  useEffect(() => {
    loadAll();
  }, []);

  async function loadAll() {
    setLoading(true);
    setError("");
    try {
      const [mixPayload, rosterPayload] = await Promise.all([
        submissionsApi.listMixes({ limit: 500 }),
        submissionsApi.listRosterApplications({ limit: 500 }),
      ]);
      setMixes(Array.isArray(mixPayload?.submissions) ? mixPayload.submissions : []);
      setApplications(Array.isArray(rosterPayload?.applications) ? rosterPayload.applications : []);
    } catch {
      setError("Could not load submissions. The backend endpoint may not be live yet.");
    } finally {
      setLoading(false);
    }
  }

  async function updateMix(id, payload) {
    try {
      await submissionsApi.updateMix(id, payload);
      await loadAll();
    } catch {
      setError("Could not update the mix submission.");
    }
  }

  async function updateRoster(id, payload) {
    try {
      await submissionsApi.updateRosterApplication(id, payload);
      await loadAll();
    } catch {
      setError("Could not update the roster application.");
    }
  }

  return (
    <div className="page space-y-6">
      <section className="card">
        <div className="md:flex md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Submissions</h1>
            <p className="mt-1 text-slate-300">
              Review mixes, tracks and talent roster applications.
            </p>
          </div>
          <div className="mt-4 flex flex-wrap gap-2 md:mt-0">
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={tab === item.id ? "btn-primary" : "btn-secondary"}
                onClick={() => setTab(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
        {error && (
          <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-red-200">
            {error}
          </div>
        )}
      </section>

      {loading && <section className="card text-slate-300">Loading submissions...</section>}

      {!loading && visible.length === 0 && (
        <section className="card text-slate-300">No submissions in this queue yet.</section>
      )}

      {!loading && visible.length > 0 && (
        <section className="grid gap-4">
          {tab === "mix"
            ? mixes.map((submission) => (
                <MixCard key={submission.id} submission={submission} onUpdate={updateMix} />
              ))
            : applications.map((application) => (
                <RosterCard key={application.id} application={application} onUpdate={updateRoster} />
              ))}
        </section>
      )}
    </div>
  );
}

function MixCard({ submission, onUpdate }) {
  const [notes, setNotes] = useState(submission.notes || "");

  return (
    <article className="card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap gap-2">
            <span className="badge">{submission.type || "MIX"}</span>
            <span className="badge">{submission.status || "PENDING"}</span>
            {submission.genre && <span className="badge">{submission.genre}</span>}
          </div>
          <h2 className="mt-2 text-xl font-semibold text-white">
            {submission.track_title || submission.artist_name}
          </h2>
          <p className="text-sm text-slate-300">
            {submission.artist_name} - {submission.email}
          </p>
          {submission.bio && <p className="mt-2 text-sm text-slate-400">{submission.bio}</p>}
          <a
            href={submission.link_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex text-sm"
          >
            Open submission link
          </a>
        </div>
        <StatusButtons current={submission.status} onChange={(status) => onUpdate(submission.id, { status })} />
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input
          className="input flex-1"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Internal notes"
        />
        <button className="btn-secondary" onClick={() => onUpdate(submission.id, { notes })}>
          Save notes
        </button>
      </div>
    </article>
  );
}

function RosterCard({ application, onUpdate }) {
  const [notes, setNotes] = useState(application.notes || "");

  return (
    <article className="card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap gap-2">
            <span className="badge">{application.status || "PENDING"}</span>
            {application.genre && <span className="badge">{application.genre}</span>}
            {application.location && <span className="badge">{application.location}</span>}
          </div>
          <h2 className="mt-2 text-xl font-semibold text-white">{application.artist_name}</h2>
          <p className="text-sm text-slate-300">{application.email}</p>
          <div className="mt-3 flex flex-wrap gap-3 text-sm">
            {application.instagram && (
              <a href={application.instagram} target="_blank" rel="noopener noreferrer">
                Instagram
              </a>
            )}
            {application.soundcloud && (
              <a href={application.soundcloud} target="_blank" rel="noopener noreferrer">
                SoundCloud
              </a>
            )}
          </div>
          {application.bio && <p className="mt-3 text-sm text-slate-400">{application.bio}</p>}
          {application.why_hlt && (
            <p className="mt-2 text-sm text-slate-300">Why HLT: {application.why_hlt}</p>
          )}
        </div>
        <StatusButtons current={application.status} onChange={(status) => onUpdate(application.id, { status })} />
      </div>
      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <input
          className="input flex-1"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Internal notes"
        />
        <button className="btn-secondary" onClick={() => onUpdate(application.id, { notes })}>
          Save notes
        </button>
      </div>
    </article>
  );
}

function StatusButtons({ current, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {STATUSES.map((status) => (
        <button
          key={status}
          type="button"
          className={current === status ? "btn-primary" : "btn-secondary"}
          onClick={() => onChange(status)}
        >
          {status}
        </button>
      ))}
    </div>
  );
}
